import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import FullSyncCard from "@/components/FullSyncCard";
import DataStatusCard from "@/components/DataStatusCard";
import SatsarCard from "@/components/SatsarCard";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Settings,
  Building2,
  Search,
  Edit2,
  Save,
  X,
  Users,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [fylker, setFylker] = useState([]);
  const [kommuner, setKommuner] = useState([]);
  const [parties, setParties] = useState([]);
  const [selectedFylke, setSelectedFylke] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingKommune, setEditingKommune] = useState(null);
  const [editForm, setEditForm] = useState({
    ordforar: "",
    styringsparti: [],
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [fylkerRes, kommunerRes, partiesRes] = await Promise.all([
        axios.get(`${API}/fylker`, { withCredentials: true }),
        axios.get(`${API}/kommuner`, { withCredentials: true }),
        axios.get(`${API}/parties`, { withCredentials: true }),
      ]);
      setFylker(fylkerRes.data);
      setKommuner(kommunerRes.data);
      setParties(partiesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Feil ved lasting av data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredKommuner = kommuner
    .filter((k) => !selectedFylke || selectedFylke === "all" || k.fylke_code === selectedFylke)
    .filter((k) => k.kommune_name.toLowerCase().includes(searchQuery.toLowerCase()));

  const openEditDialog = (kommune) => {
    setEditingKommune(kommune);
    setEditForm({
      ordforar: kommune.ordforar || "",
      styringsparti: kommune.styringsparti || [],
    });
    setEditDialogOpen(true);
  };

  const handlePartyToggle = (partyName) => {
    setEditForm((prev) => ({
      ...prev,
      styringsparti: prev.styringsparti.includes(partyName)
        ? prev.styringsparti.filter((p) => p !== partyName)
        : [...prev.styringsparti, partyName],
    }));
  };

  const handleSave = async () => {
    if (!editingKommune) return;

    try {
      setSaving(true);
      await axios.put(
        `${API}/kommuner/${editingKommune.kommune_code}`,
        editForm,
        { withCredentials: true }
      );
      toast.success("Kommune oppdatert");
      setEditDialogOpen(false);
      // Refresh kommuner
      const res = await axios.get(`${API}/kommuner`, { withCredentials: true });
      setKommuner(res.data);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Feil ved lagring");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]">
        <div className="animate-pulse text-[#4B647F]">Lastar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8FB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E9F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Settings className="w-6 h-6 text-[#103766]" />
                <h1 className="text-xl font-semibold text-[#1F2A36]">
                  Administrasjon
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/users")}
                data-testid="manage-users-btn"
                className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
              >
                <Users className="w-4 h-4 mr-2" />
                Administrer brukarar
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <FullSyncCard />

        <DataStatusCard />
        <SatsarCard />

        {/* Filters */}
        <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
                  Fylke
                </Label>
                <Select value={selectedFylke} onValueChange={setSelectedFylke}>
                  <SelectTrigger className="w-full border-[#E6E9F0]">
                    <SelectValue placeholder="Alle fylke" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle fylke</SelectItem>
                    {fylker.map((f) => (
                      <SelectItem key={f.fylke_code} value={f.fylke_code}>
                        {f.fylke_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
                  Søk
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8AA4BF]" />
                  <Input
                    type="text"
                    placeholder="Søk kommune..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 border-[#E6E9F0]"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Kommune List */}
        <Card className="bg-white border border-[#E6E9F0] rounded-lg">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#103766]" />
                Kommunar
              </CardTitle>
              <Badge variant="outline">{filteredKommuner.length} kommunar</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-[#E6E9F0]">
                {filteredKommuner.map((kommune) => (
                  <div
                    key={kommune.kommune_code}
                    className="px-6 py-4 flex items-center justify-between hover:bg-[#F6F8FB]/50 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-[#1F2A36]">
                        {kommune.kommune_name}
                      </p>
                      <p className="text-sm text-[#8AA4BF]">
                        {kommune.fylke_name} • {kommune.kommune_code}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {kommune.ordforar && (
                          <Badge variant="secondary" className="text-xs">
                            Ordførar: {kommune.ordforar}
                          </Badge>
                        )}
                        {kommune.styringsparti?.map((party) => {
                          const match = parties.find(
                            (p) =>
                              p.name.toLowerCase() === party.toLowerCase() ||
                              p.short_name.toLowerCase() === party.toLowerCase()
                          );
                          return (
                            <span
                              key={party}
                              data-testid={`admin-parti-${kommune.kommune_code}-${match?.party_id || party}`}
                              className="inline-flex items-center gap-1.5 rounded-md border border-[#E6E9F0] bg-white px-2 py-1 text-xs text-[#1F2A36]"
                            >
                              {match?.logo_url ? (
                                <img
                                  src={match.logo_url}
                                  alt={match.name}
                                  className={`object-contain ${
                                    match?.logo_type === "wordmark"
                                      ? "w-[92px] h-auto max-h-9"
                                      : "h-5 w-auto max-w-[24px]"
                                  }`}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : null}
                              {(!match?.logo_url || match?.logo_type === "symbol") && party}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(kommune)}
                      data-testid={`edit-${kommune.kommune_code}`}
                      className="text-[#4B647F] hover:text-[#103766]"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1F2A36]">
              <Building2 className="w-5 h-5 text-[#103766]" />
              Rediger {editingKommune?.kommune_name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Ordførar */}
            <div>
              <Label
                htmlFor="ordforar"
                className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]"
              >
                Ordførar
              </Label>
              <Input
                id="ordforar"
                value={editForm.ordforar}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, ordforar: e.target.value }))
                }
                placeholder="Skriv inn ordførarens navn"
                className="mt-2 border-[#E6E9F0] bg-white text-[#1F2A36]"
                data-testid="ordforar-input"
              />
            </div>

            {/* Styringsparti */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3 block">
                Styringsparti
              </Label>
              <div className="grid grid-cols-2 gap-3 max-h-[300px] overflow-y-auto">
                {parties.map((party) => (
                  <div
                    key={party.party_id}
                    className="flex items-center space-x-3 p-2 rounded-lg border border-[#E6E9F0] hover:bg-[#F6F8FB] transition-colors bg-white"
                  >
                    <Checkbox
                      id={party.party_id}
                      checked={editForm.styringsparti.includes(party.name)}
                      onCheckedChange={() => handlePartyToggle(party.name)}
                      data-testid={`party-${party.party_id}`}
                      className="border-[#8AA4BF] data-[state=checked]:bg-[#103766] data-[state=checked]:border-[#103766]"
                    />
                    <label
                      htmlFor={party.party_id}
                      className="text-sm cursor-pointer flex-1 flex items-center gap-2 text-[#1F2A36]"
                    >
                      {party.logo_url ? (
                        <img
                          src={party.logo_url}
                          alt={party.name}
                          className={`object-contain ${
                            party.logo_type === "wordmark"
                              ? "w-[120px] h-auto max-h-12"
                              : "h-7 w-auto max-w-[32px]"
                          }`}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : null}
                      {(!party.logo_url || party.logo_type === "symbol") && (
                        <span className="truncate">{party.name}</span>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-[#E6E9F0] text-[#4B647F]"
            >
              <X className="w-4 h-4 mr-2" />
              Avbryt
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-btn"
              className="bg-[#103766] hover:bg-[#1B4E8A] text-white"
            >
              {saving ? (
                "Lagrar..."
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Lagre
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
